import React, { useState, useMemo, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, doc, deleteDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

// ── "Seen" tracking (localStorage) ────────────────────────────────────────────
// Each lead's id is added to this set after it's been in the viewport for ~1.5s.
const SEEN_KEY = 'ndQualifiedLeadsV2_seenIds';
function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function saveSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch {}
}

// ── Keyword categories ────────────────────────────────────────────────────────
// Groups search keywords by life-event category. Filter by category to see
// all leads matching ANY keyword in the category, or drill into individual
// keywords for granular filtering.

const KEYWORD_CATEGORIES = [
  {
    key: 'lifeTransitions',
    label: 'Life Transitions',
    emoji: '🔄',
    color: '#c084fc',
    keywords: ['divorce', 'separated', 'ex husband', 'ex wife', 'downsizing', 'empty nest', 'kids moved out', 'smaller home', 'new chapter'],
  },
  {
    key: 'relocation',
    label: 'Relocation',
    emoji: '🚛',
    color: '#60a5fa',
    keywords: ['moving out of area', 'moving out of state', 'moving away', 'moving for work', 'relocating', 'leaving the area', 'moving in with family'],
  },
  {
    key: 'financialDistress',
    label: 'Financial Distress',
    emoji: '💼',
    color: '#fb923c',
    keywords: ['foreclosure', 'short sale', 'bankruptcy', 'behind on mortgage', 'behind on bills', 'must sell', 'cant afford', 'lost my job', 'laid off', 'medical bills', 'back taxes', 'tax lien', 'sell house fast', 'cash offer', 'selling as is'],
  },
  {
    key: 'estate',
    label: 'Estate & Inheritance',
    emoji: '🕊️',
    color: '#94a3b8',
    keywords: ['estate sale', 'inherited house', 'inherited property', 'probate', 'executor of estate', 'mom passed away', 'dad passed away'],
  },
  {
    key: 'health',
    label: 'Health & Elderly',
    emoji: '🏥',
    color: '#22d3ee',
    keywords: ['assisted living', 'nursing home'],
  },
  {
    key: 'tiredLandlords',
    label: 'Tired Landlords',
    emoji: '🏘️',
    color: '#f472b6',
    keywords: ['tired landlord'],
  },
  {
    key: 'repairBurdened',
    label: 'Repair-Burdened',
    emoji: '🔨',
    color: '#fbbf24',
    keywords: ['fixer upper', 'needs too many repairs'],
  },
];

// Reverse lookup: keyword → category (for inferring category from a lead's keywords)
const KEYWORD_TO_CATEGORY = (() => {
  const map = {};
  KEYWORD_CATEGORIES.forEach(cat => cat.keywords.forEach(k => { map[k] = cat; }));
  return map;
})();

// ── Distress signal categories (regex-detected from post text) ───────────────

const SIGNALS = [
  { key: 'foreclosure', label: 'Foreclosure', emoji: '🔥', color: '#dc2626', glow: 'rgba(220,38,38,0.08)',  rx: /\b(forecl(osure|osing)?|behind on (the )?(mortgage|payment|house)|losing (the |my |our )?(house|home)|sheriff sale|loan mod(ification)?|deed in lieu|notice of default|past due mortgage)\b/i },
  { key: 'divorce',     label: 'Divorce',     emoji: '💔', color: '#7c3aed', glow: 'rgba(124,58,237,0.08)', rx: /\b(divorc(e|ed|ing)?|separat(ed|ing|ion)|ex[- ]?(husband|wife|spouse|partner)|splitting up|custody battle)\b/i },
  { key: 'downsizing',  label: 'Downsizing',  emoji: '🏠', color: '#059669', glow: 'rgba(5,150,105,0.08)',  rx: /\b(downsiz(e|ing)|empty.?nest(er)?|too (big|large) (a |of a |for)|smaller (home|place|house|condo|apartment)|kids (have )?(moved out|grown)|right.?sizing)\b/i },
  { key: 'bankruptcy',  label: 'Bankruptcy',  emoji: '⚖️', color: '#d97706', glow: 'rgba(217,119,6,0.08)',  rx: /\b(bankrupt(cy)?|chapter (7|13)|debt relief|consumer proposal|debt consolidat)/i },
  { key: 'estate',      label: 'Estate',      emoji: '🕊️', color: '#475569', glow: 'rgba(71,85,105,0.08)',  rx: /\b(passed away|inherit(ed|ance)|estate (sale|attorney|planning|lawyer)|probat(e|ing)|deceased|late (mother|father|husband|wife|mom|dad|parent|spouse))\b/i },
  { key: 'financial',   label: 'Financial',   emoji: '💼', color: '#ea580c', glow: 'rgba(234,88,12,0.08)',  rx: /\b(lost (my |our )?job|laid off|unemploy(ed|ment)?|can'?t afford|financial (hardship|trouble|stress)|behind on bills|struggling financially|medical (bills|debt))\b/i },
  { key: 'relocation',  label: 'Relocating',  emoji: '🚛', color: '#2563eb', glow: 'rgba(37,99,235,0.08)',  rx: /\b(relocat(e|ing|ion)|moving (out of (state|town|area|the area)|away|to (another |a new |a different ))|leaving (the area|town)|job transfer|new job in|out.?of.?state move|cross.?country move|selling (our |my |the )?(home|house|place) (because|since|due to)?\s*(we|i|i'?m|we'?re).{0,30}mov)\b/i },
  { key: 'health',      label: 'Health',      emoji: '🏥', color: '#0891b2', glow: 'rgba(8,145,178,0.08)',  rx: /\b(assisted living|nursing home|memory care|hospice|moving (in with|near|closer to) (family|kids|son|daughter|children|parents)|elderly parent|caregiv(er|ing))\b/i },
  { key: 'mustsell',    label: 'Must Sell',   emoji: '⚡', color: '#be185d', glow: 'rgba(190,24,93,0.08)',  rx: /\b(must sell|need to sell (fast|quick|asap|soon)|motivated seller|cash (only|offer)|as.?is|sell my house fast|quick (sale|close))\b/i },
  { key: 'lawyer',      label: 'Lawyer ask',  emoji: '⚖️', color: '#6b7280', glow: 'rgba(107,114,128,0.08)',rx: /\b((divorce|family|estate|bankruptcy|real estate|foreclosure) (attorney|lawyer)|need (a |an )?(attorney|lawyer)|legal (advice|help))\b/i },
];

function detectSignals(text) {
  const hits = [];
  for (const sig of SIGNALS) if (sig.rx.test(text || '')) hits.push(sig);
  return hits;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function parsePostDate(entry) {
  if (entry.postDate?.toDate) { try { return entry.postDate.toDate(); } catch {} }
  if (entry.postDate?.seconds) { try { return new Date(entry.postDate.seconds * 1000); } catch {} }
  return null;
}
export function parseCapturedAt(entry) {
  if (entry.capturedAt) { try { return new Date(entry.capturedAt); } catch {} }
  if (entry.scrapedAt?.toDate) { try { return entry.scrapedAt.toDate(); } catch {} }
  return null;
}
export function parseFirstApprovedAt(entry) {
  if (entry.firstApprovedAt?.toDate) { try { return entry.firstApprovedAt.toDate(); } catch {} }
  if (entry.firstApprovedAt?.seconds) { try { return new Date(entry.firstApprovedAt.seconds * 1000); } catch {} }
  return null;
}

function timeAgo(d) {
  if (!d) return '';
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

// Escape a string for use inside a RegExp.
function reEscape(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

// A search result reaches us flattened into one string: the post (usually an excerpt
// around the keyword), its reaction and comment counts, then the comment that matched,
// if any:
//   "We're moving ... Thank you. 5 15 Wayne Johnson · Franklinton, NC · 24 Aug Not my ex wife"
// Businesses comment with no place: "6 34 S Sem Build & Renovate · 13 May Hi Roberta".
// A card starting with "Name · Place · date" holds only a comment, not the post.
// Same rules as cleanSnippet in scraper-functions/fb-scraper/aiVetter.js.
const WHEN = '(?:\\d+ (?:min|mins|hr|hrs|hour|hours|day|days|wk|wks|week|weeks|mo|mos|month|months) ago|\\d{1,2} [A-Z][a-z]{2}(?: \\d{2,4})?|[A-Z][a-z]{2} \\d{1,2}(?:, \\d{4})?|Yesterday|Today|Just now)(?![A-Za-z])';
const COMMENTER_BYLINE = new RegExp(`( · [^·]{1,60})? · ${WHEN}`, 'g');
const STARTS_WITH_COMMENT = new RegExp(`^[^·]{1,60} · [^·]{1,60} · ${WHEN}\\s*`);
const OWN_HEADER = new RegExp(`^[^·]{1,130} · ${WHEN}\\s*`);
const COUNTS_AT_END = /(?:\s+\d{1,6}){1,2}$/;
const COMMENTER_NAME_MAX = 80;

// Where the first comment begins (at its counts), or -1. A comment always has a count
// in front of the byline, which is what tells it from a date in the post's own words.
function commentStart(text) {
  for (const m of text.matchAll(COMMENTER_BYLINE)) {
    const before = text.slice(0, m.index);
    const counts = [...before.matchAll(/(?:^|\s)(\d{1,6})(?=\s|$)/g)];
    for (let i = counts.length - 1; i >= 0; i--) {
      const nameLen = before.length - (counts[i].index + counts[i][0].length);
      if (nameLen > COMMENTER_NAME_MAX) break;
      if (nameLen < 2) continue; // part of the name: "House Wash 123"
      const prev = counts[i - 1];
      return prev && prev.index + prev[0].length === counts[i].index ? prev.index : counts[i].index;
    }
  }
  return -1;
}

// A card's text up to its first comment, without "Show less" / "Show more".
function textBeforeComments(entry) {
  const t = (entry.postText || '').replace(/\s+/g, ' ').trim();
  const at = commentStart(t);
  return (at > 0 ? t.slice(0, at) : t).replace(/\s*Show\s+(less|more)\s*\.?\s*$/i, '').trim();
}

// Aggressive regex-only prefix stripper: catches "<Neighborhood> · <Date> <text>"
// at the start of the post body, regardless of what entry.neighborhood/date hold.
// Handles: "Columbia Heights Northwest · 5 Apr ...", "Mt. Pleasant · 6 days ago ...",
// "Battery lane · 2 days ago ...", "Lewisdale · 15 May ...", "Silver Spring · 1d ago ...".
// Also handles an optional "AvatarInitial AuthorName " prefix before the neighborhood.
const ND_HEADER_PREFIX_RX = new RegExp(
  '^' +
    // Optional avatar initial + author name (1-4 capitalized words)
    '(?:[A-Z]\\s+)?(?:(?:[A-Z][\\w\'’.]*\\.?\\s+){1,4})?' +
    // Neighborhood: starts with a capitalized word, then 0-5 more words (any case)
    '([A-Z][\\w\'’.\\-]*(?:\\s+[\\w\'’.\\-]+){0,5})' +
    // Separator
    '\\s*[·•]\\s*' +
    // Date/time: relative ("6 days ago", "1d ago"), absolute ("5 Apr", "15 May"),
    // or shorthand ("yesterday", "today", "just now")
    '(?:' +
      '\\d+\\s*(?:min|mins?|m|h|hrs?|hours?|d|days?|w|wks?|weeks?|mo|months?)\\s*ago' +
      '|yesterday|today|just\\s*now' +
      '|\\d{1,2}\\s+[A-Za-z]{3,9}' +
      '|[A-Za-z]{3,9}\\s+\\d{1,2}' +
    ')' +
    '\\s+',
  'i'
);

// The post's own words from a stored postText, '' when the card holds only a comment.
// Its header can only be at the start: a comment posted the same day repeats the date,
// so searching further in would show the comment in place of the post.
function postOwnWords(entry) {
  const head = textBeforeComments(entry);
  if (!head || STARTS_WITH_COMMENT.test(head)) return '';

  let text = head.replace(OWN_HEADER, '').trim();
  if (text === head) text = head.replace(ND_HEADER_PREFIX_RX, '').trim();

  // Fallback: strip "AvatarInitial AuthorName " prefix + optional neighborhood + date
  if (text === head && entry.author && entry.author !== 'Unknown') {
    const rx = new RegExp(`^(?:[A-Z]\\s+)?${reEscape(entry.author)}\\s+`, 'i');
    const after = head.replace(rx, '').trim();
    if (after !== head) {
      let r = after;
      if (entry.neighborhood) r = r.replace(new RegExp(`^${reEscape(entry.neighborhood)}\\s*[·•]?\\s*`, 'i'), '').trim();
      r = r.replace(/^(?:\d+\s*(?:min|mins|m|h|hr|hrs|hour|hours|d|day|days|w|wk|week|weeks|mo|month|months)\s*ago|yesterday|today|just now|[A-Z][a-z]{2,8}\s+\d{1,2}|\d{1,2}\s+[A-Z][a-z]{2,8})\s*/i, '').trim();
      if (r) text = r;
    }
  }

  return text.replace(COUNTS_AT_END, '').trim();
}

// What a row shows: the post's own words, or the comment when that's all the card holds.
export function cleanPostText(entry) {
  return postOwnWords(entry) || textBeforeComments(entry).replace(STARTS_WITH_COMMENT, '').replace(COUNTS_AT_END, '').trim();
}

function fmtDate(d) {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
export const PRESETS = [
  { label: 'All',       key: 'all' },
  { label: 'Today',     key: 'today' },
  { label: '7d',        key: '7d' },
  { label: '30d',       key: '30d' },
];
export function getPresetRange(key) {
  const now = new Date();
  const sod = startOfDay(now);
  if (key === 'today') return { from: sod, to: now };
  if (key === '7d')  { const s = new Date(sod); s.setDate(s.getDate() - 6);  return { from: s, to: now }; }
  if (key === '30d') { const s = new Date(sod); s.setDate(s.getDate() - 29); return { from: s, to: now }; }
  return null;
}

// ── Ranking (shared with NdSellerDash's "Highest scoring" tab) ────────────────
// Both pages rank through these, so the two orders can't drift apart.

// Client-side dedupe (two-pass)
// Pass 1: collapse by best available stable ID (postId > profileId+fp > fp).
// Pass 2: collapse anything left that shares a text fingerprint, even if the
//          postIds differ — catches duplicate Nextdoor framings of the same
//          post (e.g. user-authored vs "Ask <Community>" wrapper).
export function dedupeSellerLeads(rawLeads) {
  function fingerprint(text) {
    return (text || '').toLowerCase().replace(/\s+/g, ' ').replace(/[^\w\s]/g, '').trim().slice(0, 140);
  }
  function primaryKey(l) {
    if (l.postId) return `pid:${l.postId}`;
    const fp = fingerprint(l.postText);
    if (l.profileId)  return `prf:${l.profileId}::${fp}`;
    if (l.profileUrl) return `url:${l.profileUrl}::${fp}`;
    if (l.author && l.author !== 'Unknown') return `auth:${l.author.toLowerCase()}::${fp}`;
    return `fp:${fp}`;
  }
  function merge(into, from) {
    const kws = from.keywords || (from.keyword ? [from.keyword] : []);
    into.keywords = [...new Set([...(into.keywords || []), ...kws])];
    if (!into.postId && from.postId) into.postId = from.postId;
    if (!into.postUrl && from.postUrl) into.postUrl = from.postUrl;
    if (!into.profileId && from.profileId) into.profileId = from.profileId;
    if (!into.profileUrl && from.profileUrl) into.profileUrl = from.profileUrl;
    if (!into.profilePhoto && from.profilePhoto) into.profilePhoto = from.profilePhoto;
    if (!into.neighborhood && from.neighborhood) into.neighborhood = from.neighborhood;
    if (!into.postDate && from.postDate) into.postDate = from.postDate;
    if (!into.postDateLabel && from.postDateLabel) into.postDateLabel = from.postDateLabel;
    // Prefer a real author over "Unknown", and over a commenter's byline: a comment-only
    // card stores "Name · Place · date" as its author, and that person didn't write the post.
    const authorRank = (a) => (!a || a === 'Unknown' ? 0 : /\s[·•]\s/.test(a) ? 1 : 2);
    if (authorRank(from.author) > authorRank(into.author)) {
      into.author = from.author;
      if (from.profilePhoto) into.profilePhoto = from.profilePhoto;
    }
    // The most of the post's own words wins, so a long comment can't replace the post.
    const fromWords = postOwnWords(from).length, intoWords = postOwnWords(into).length;
    if (fromWords > intoWords || (fromWords === intoWords && (from.postText || '').length > (into.postText || '').length)) {
      into.postText = from.postText;
    }
    const nd = parseCapturedAt(from), od = parseCapturedAt(into);
    if (nd && (!od || nd > od)) { into.capturedAt = from.capturedAt; into.scrapedAt = from.scrapedAt; }
    into.foundBy = [...new Set([...(into.foundBy || []), ...(from.foundBy || [])])];
    into._docs.push(...(from._docs || [from]));
  }

  // The first doc of a group becomes its row (id, DM status, verdict, score), so a
  // doc carrying DM activity goes first (sent before sending/skipped/failed), then an
  // approved visible one, one holding the post's own words before a comment-only card.
  // One post is
  // often several docs (doc ids hash viewer-dependent text), and without this a
  // DMed post could resurface as an un-DMed twin with its own Launch DM button.
  // Each row keeps every doc it merged in `_docs`.
  const DM_RANK = { sent: 0, sending: 1, skipped: 2, failed: 3 };
  const rank = (l) => DM_RANK[l.sellerDmStatus] ?? (l.aiVerdict === 'good' && l.hidden !== true ? (postOwnWords(l) ? 4 : 4.5) : 5);
  const ordered = rawLeads.map((l, i) => [l, i, rank(l)]).sort((a, b) => a[2] - b[2] || a[1] - b[1]).map(([l]) => l);

  // Pass 1
  const byPrimary = new Map();
  for (const l of ordered) {
    const k = primaryKey(l);
    const kws = l.keywords || (l.keyword ? [l.keyword] : []);
    const ex = byPrimary.get(k);
    if (!ex) byPrimary.set(k, { ...l, keywords: [...new Set(kws)], _docs: [l] });
    else merge(ex, l);
  }

  // Pass 2 — collapse anything sharing a strong fingerprint
  const byFingerprint = new Map();
  for (const l of byPrimary.values()) {
    const fp = fingerprint(l.postText);
    // Only collapse on fingerprint if it's a meaningful length (avoid joining tiny snippets)
    if (fp.length < 40) { byFingerprint.set(`short:${l.postId || Math.random()}`, l); continue; }
    const ex = byFingerprint.get(fp);
    if (!ex) byFingerprint.set(fp, l);
    else merge(ex, l);
  }

  return [...byFingerprint.values()];
}

export function leadInPresetRange(l, dateRange) {
  if (!dateRange) return true;
  const d = parsePostDate(l) || parseCapturedAt(l);
  if (!d) return false;
  if (dateRange.from && d < dateRange.from) return false;
  if (dateRange.to   && d > dateRange.to)   return false;
  return true;
}

export function compareByScore(a, b) {
  // Primary sort: AI score (highest first) — undefined treated as 0 so legacy/un-scored leads sink
  const sa = Number.isFinite(a.aiScore) ? a.aiScore : 0;
  const sb = Number.isFinite(b.aiScore) ? b.aiScore : 0;
  if (sb !== sa) return sb - sa;
  // Tiebreaker: most recent first
  const da = parsePostDate(a) || parseCapturedAt(a);
  const db2 = parsePostDate(b) || parseCapturedAt(b);
  return (db2 || 0) - (da || 0);
}

// Posts first approved in [from, to), for NdSellerDash's "New" tab. firstApprovedAt
// is write-once per doc, so unlike scrapedAt a re-find can't move it. But a re-find
// can land in a fresh doc (doc ids hash viewer-dependent text), so a post's time is
// the earliest across ALL its docs, a doc flagged 'bad' since then included. A post
// shows only while it's still approved: AI-good and not hidden, as on the other tabs.
// A good doc with no stamp means the post can't be dated, so it's counted in
// `unstamped` rather than called new.
export function firstApprovedInWindow(rawLeads, from, to) {
  const rows = [];
  let unstamped = 0;
  for (const group of dedupeSellerLeads(rawLeads)) {
    const approved = group._docs.filter((d) => d.aiVerdict === 'good' && d.hidden !== true);
    if (group.hidden === true || !approved.length) continue;
    if (group._docs.some((d) => d.aiVerdict === 'good' && !parseFirstApprovedAt(d))) {
      unstamped += 1;
      continue;
    }
    const firstApprovedAt = new Date(Math.min(...group._docs.map(parseFirstApprovedAt).filter(Boolean)));
    if (firstApprovedAt >= from && firstApprovedAt < to) rows.push({ group, approved, firstApprovedAt });
  }
  return { rows, unstamped };
}

// ── Theme tokens (light mode) ─────────────────────────────────────────────────

const T = {
  bg:        '#f8f9fb',
  bgSidebar: '#ffffff',
  bgCard:    '#ffffff',
  bgCardHover: '#fafbfc',
  border:    'rgba(0,0,0,0.06)',
  borderHi:  'rgba(0,0,0,0.12)',
  text:      '#0f172a',
  textMid:   '#64748b',
  textDim:   '#94a3b8',
  accent:    '#7c3aed',
  accentGlow:'rgba(124,58,237,0.08)',
};

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ name, src, size = 40 }) {
  const [err, setErr] = useState(false);
  const initials = name && name !== 'Unknown'
    ? name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const palette = ['#a78bfa','#f472b6','#fbbf24','#34d399','#60a5fa','#fb923c','#22d3ee','#f87171'];
  const bg = palette[name ? Math.abs(name.charCodeAt(0)) % palette.length : 0];

  if (src && !err) {
    return (
      <img src={src} alt={name || ''} onError={() => setErr(true)} style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        objectFit: 'cover',
        boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 2px 8px rgba(0,0,0,0.4)`,
      }} />
    );
  }

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${bg}, ${bg}dd)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: '#fff', letterSpacing: '-0.5px',
      boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 2px 8px ${bg}44`,
    }}>{initials}</div>
  );
}

// ── Signal Badge ──────────────────────────────────────────────────────────────

function SignalBadge({ signal }) {
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
      color: signal.color,
      background: signal.glow,
      border: `1px solid ${signal.color}33`,
      whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4,
      letterSpacing: '0.02em',
    }}>
      <span style={{ fontSize: 11 }}>{signal.emoji}</span>
      {signal.label}
    </span>
  );
}

// ── Keyword chip (in row) ─────────────────────────────────────────────────────

function KeywordChip({ keyword }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6,
      color: T.textMid, background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${T.border}`, whiteSpace: 'nowrap',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    }}>
      {keyword}
    </span>
  );
}

// ── Verify Owner Button ───────────────────────────────────────────────────────
// Calls the local messageServer's /verify-owner endpoint (which proxies to
// ReportAll). Caches the result on the lead's Firestore doc so re-renders
// don't re-hit the API. Shows inline summary on the card after verification.

const MSG_SERVER = process.env.REACT_APP_MSG_SERVER || 'http://localhost:3002';

function fmtUSD(n) {
  if (!n) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function VerifyOwnerButton({ entry }) {
  const [status, setStatus]   = useState('idle'); // idle | loading | error
  const [state, setState]     = useState('VA');
  const [err, setErr]         = useState('');
  const [showRetry, setShowRetry] = useState(false); // user wants to retry a different state

  const cached = entry.ownerVerified || null;
  // Show cached result inline if present (unless user explicitly clicked retry, or a fresh lookup is in progress)
  if (cached && cached.checkedAt && !showRetry && status !== 'loading') {
    const cachedFound = cached.found;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
        <div style={{
          padding: '8px 10px', borderRadius: 7, fontSize: 12, lineHeight: 1.45,
          background: cachedFound ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${cachedFound ? '#a7f3d0' : '#fecaca'}`,
          color: cachedFound ? '#065f46' : '#991b1b',
        }}>
          {cachedFound ? (
            <>
              <strong>🏠 Owns {cached.count} {cached.count === 1 ? 'property' : 'properties'}</strong>
              {cached.totalValue > 0 && <> · <strong>{fmtUSD(cached.totalValue)} total value</strong></>}
              {cached.ownerOccupiedCount > 0 && (
                <> · {cached.ownerOccupiedCount} owner-occupied</>
              )}
              <div style={{ marginTop: 6, fontSize: 11, color: '#047857' }}>
                {(cached.properties || []).slice(0, 3).map((p, i) => (
                  <div key={i}>
                    📍 {p.address}, {p.city}, {p.state}
                    {p.ownerOccupied ? ' · 🏠 occupied' : ' · 🏘️ rental?'}
                    {p.marketValue > 0 && ` · ${fmtUSD(p.marketValue)}`}
                    {p.transDate && ` · bought ${p.transDate.slice(0,4)}`}
                  </div>
                ))}
                {cached.properties && cached.properties.length > 3 && (
                  <div style={{ color: '#6b7280' }}>… and {cached.properties.length - 3} more</div>
                )}
              </div>
            </>
          ) : (
            <>
              <strong>❌ No record in {cached.region}</strong>
              <div style={{ marginTop: 4, fontSize: 11 }}>
                Likely renter, or owns in a different state.
              </div>
            </>
          )}
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6 }}>
            Checked {new Date(cached.checkedAt).toLocaleDateString()} · {cached.queriedOwnerFormat}
          </div>
        </div>
        <button
          onClick={() => setShowRetry(true)}
          style={{
            alignSelf: 'flex-start', padding: '3px 8px', fontSize: 10, borderRadius: 5,
            background: 'none', border: `1px solid ${T.border}`, color: T.textMid, cursor: 'pointer',
          }}
        >
          🔄 Re-check in different state
        </button>
      </div>
    );
  }

  async function handleClick() {
    if (status === 'loading') return;
    setStatus('loading'); setErr('');
    try {
      const r = await fetch(`${MSG_SERVER}/verify-owner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: entry.author, state, leadId: entry.id }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      // Success — the Firestore listener will push the new ownerVerified into entry
      // shortly and the cached display will render automatically. Reset state.
      setStatus('idle');
      setShowRetry(false);
    } catch (e) {
      setStatus('error');
      setErr(e.message.includes('fetch') ? `Lookup server unreachable (${MSG_SERVER})` : e.message);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
      <button onClick={handleClick} disabled={status === 'loading'} style={{
        padding: '5px 11px', fontSize: 11, fontWeight: 600, borderRadius: 6,
        border: '1px solid #d1d5db', cursor: status === 'loading' ? 'default' : 'pointer',
        background: status === 'loading' ? '#f5f5f5' : '#fff',
        color: status === 'loading' ? '#bbb' : '#1f2937',
        whiteSpace: 'nowrap',
      }}>
        {status === 'loading' ? 'Checking…' : '🏠 Verify Owner'}
      </button>
      <select value={state} onChange={e => setState(e.target.value)} disabled={status === 'loading'} style={{
        padding: '4px 6px', fontSize: 11, borderRadius: 5, border: '1px solid #d1d5db',
        background: status === 'loading' ? '#f5f5f5' : '#fff', color: '#374151',
      }}>
        <option value="VA">VA</option>
        <option value="MD">MD</option>
        <option value="DC">DC</option>
        <option value="WV">WV</option>
        <option value="PA">PA</option>
      </select>
      {err && <span style={{ fontSize: 11, color: '#dc2626' }}>{err}</span>}
    </div>
  );
}

// ── Lead Row ──────────────────────────────────────────────────────────────────

const POST_TRUNCATE_AT = 280; // chars — collapse longer posts behind "show more"

function LeadRow({ entry, isNew, onMarkSeen, onDelete }) {
  const [hover, setHover]       = useState(false);
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef(null);
  const cleanedText = cleanPostText(entry);
  const signals  = detectSignals(cleanedText);
  const topSig   = signals[0];
  const postDate = parsePostDate(entry) || parseCapturedAt(entry);

  // Auto-mark as seen after 1.5s of being visible in the viewport
  useEffect(() => {
    if (!isNew || !cardRef.current) return;
    let timer = null;
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e && e.isIntersecting && e.intersectionRatio > 0.5) {
        timer = setTimeout(() => onMarkSeen(entry.id), 1500);
      } else if (timer) {
        clearTimeout(timer); timer = null;
      }
    }, { threshold: [0, 0.5, 1] });
    obs.observe(cardRef.current);
    return () => { obs.disconnect(); if (timer) clearTimeout(timer); };
  }, [isNew, entry.id, onMarkSeen]);

  const isLong = cleanedText.length > POST_TRUNCATE_AT;
  const displayText = !isLong || expanded
    ? cleanedText
    : cleanedText.slice(0, POST_TRUNCATE_AT).trimEnd() + '…';

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? T.bgCardHover : T.bgCard,
        border: `1px solid ${isNew ? 'rgba(124,58,237,0.35)' : (hover ? T.borderHi : T.border)}`,
        borderLeft: topSig ? `3px solid ${topSig.color}` : `3px solid ${T.border}`,
        borderRadius: 10,
        padding: '14px 16px',
        margin: '8px 24px',
        transition: 'all 0.15s',
        boxShadow: isNew
          ? '0 0 0 3px rgba(124,58,237,0.10), 0 2px 8px rgba(124,58,237,0.08)'
          : (hover ? '0 2px 8px rgba(0,0,0,0.04)' : '0 1px 2px rgba(0,0,0,0.02)'),
        position: 'relative',
      }}
    >
      {/* Header row: avatar + name + time + NEW badge + delete */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Avatar name={entry.author} src={entry.profilePhoto} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.2 }}>
              {entry.author || 'Unknown'}
            </div>
            {(() => {
              const tier = entry.aiTier;
              const score = Number.isFinite(entry.aiScore) ? entry.aiScore : null;
              if (!tier || tier === 'none') return null;
              const TIERS = {
                hot:   { label: '🔥 Hot',   color: '#dc2626', bg: 'rgba(220,38,38,0.10)', border: 'rgba(220,38,38,0.30)' },
                warm:  { label: '👍 Warm',  color: '#d97706', bg: 'rgba(217,119,6,0.10)', border: 'rgba(217,119,6,0.30)' },
                maybe: { label: '💭 Maybe', color: '#6b7280', bg: 'rgba(107,114,128,0.10)', border: 'rgba(107,114,128,0.30)' },
              };
              const t = TIERS[tier]; if (!t) return null;
              return (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                  color: t.color, background: t.bg, border: `1px solid ${t.border}`,
                  whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3,
                }}>
                  {t.label}
                  {score != null && <span style={{ opacity: 0.7, fontSize: 9 }}>{score}/10</span>}
                </span>
              );
            })()}
            {isNew && (
              <span style={{
                fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
                background: T.accent, color: '#fff',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                boxShadow: '0 1px 3px rgba(124,58,237,0.35)',
              }}>NEW</span>
            )}
          </div>
          {postDate && (
            <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
              {timeAgo(postDate)} ago · {fmtDate(postDate)}
            </div>
          )}
        </div>

        {/* Delete button — only shows on hover */}
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete lead from "${entry.author}"?`)) onDelete(entry.id);
            }}
            title="Delete this lead"
            style={{
              opacity: hover ? 1 : 0,
              transition: 'opacity 0.15s, background 0.12s, color 0.12s',
              border: 'none', background: 'transparent', cursor: 'pointer',
              width: 26, height: 26, borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.textDim, fontSize: 16, lineHeight: 1, padding: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textDim; }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Pills row: signals + neighborhood + keywords */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {signals.map(s => <SignalBadge key={s.key} signal={s} />)}
        {entry.neighborhood && (
          <span style={{
            fontSize: 10.5, fontWeight: 500, padding: '3px 9px', borderRadius: 20,
            color: T.textMid, background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${T.border}`, whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            📍 {entry.neighborhood}
          </span>
        )}
        {(entry.keywords || []).map(k => (
          <span key={k} style={{
            fontSize: 10.5, fontWeight: 500, padding: '3px 9px', borderRadius: 20,
            color: T.textMid, background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${T.border}`, whiteSpace: 'nowrap',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            "{k}"
          </span>
        ))}
      </div>

      {/* Post text — the star of the show */}
      <p style={{
        margin: 0, fontSize: 14, color: T.text, lineHeight: 1.55, whiteSpace: 'pre-wrap',
        fontWeight: 400, letterSpacing: '-0.005em',
      }}>
        {displayText}
        {isLong && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              marginLeft: 6, padding: 0, border: 'none', background: 'none',
              color: T.accent, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>

      {/* AI verdict reason */}
      {entry.aiReason && (
        <div style={{
          marginTop: 10, padding: '7px 11px',
          background: T.accentGlow,
          border: `1px solid rgba(124,58,237,0.18)`,
          borderRadius: 7,
          fontSize: 12, color: T.textMid,
          fontStyle: 'italic', lineHeight: 1.4,
        }}>
          <span style={{ color: T.accent, fontWeight: 600, fontStyle: 'normal' }}>🤖 AI:</span>{' '}
          {entry.aiReason}
        </div>
      )}

      {/* Verify Owner via ReportAll */}
      {entry.author && entry.author !== 'Unknown' && (
        <VerifyOwnerButton entry={entry} />
      )}

      {/* Footer: view post link */}
      {entry.postUrl && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href={entry.postUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: '#60a5fa', textDecoration: 'none', fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            View {entry.author && entry.author !== 'Unknown' ? `${entry.author.split(' ')[0]}'s` : 'this'} post
            <span style={{ fontSize: 10 }}>↗</span>
          </a>
          {entry.profileUrl && (
            <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 11, color: T.textMid, textDecoration: 'none', fontWeight: 500 }}>
              View profile
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sidebar item ──────────────────────────────────────────────────────────────

function SidebarItem({ active, onClick, label, count, color, emoji }) {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', cursor: 'pointer', borderRadius: 7, userSelect: 'none',
        background: active ? (color ? `${color}18` : T.accentGlow)
                   : hover ? 'rgba(255,255,255,0.04)' : 'transparent',
        color: active ? (color || T.accent) : T.textMid,
        fontSize: 13, fontWeight: active ? 600 : 500,
        border: active && color ? `1px solid ${color}33` : '1px solid transparent',
        transition: 'all 0.12s',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {emoji && <span style={{ fontSize: 13 }}>{emoji}</span>}
        {color && !emoji && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0,
          boxShadow: active ? `0 0 8px ${color}` : 'none' }} />}
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </span>
      <span style={{ fontSize: 11, color: active ? (color || T.accent) : T.textDim, fontVariantNumeric: 'tabular-nums',
        fontWeight: 600 }}>{count}</span>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

// ── Scrape Health panel ───────────────────────────────────────────────────────
// Shows the two morning runs (VA 9 AM, VA-2 10 AM ET) plus full run history from
// ndSellerScrapeRuns, so you can see which scrapes worked and how many leads each
// pulled. Statuses: success (completed), partial (saved leads but cut off),
// failed (0 leads / crashed), running.
const HEALTH_STATUS = {
  success: { bg: '#dcfce7', fg: '#15803d', dot: '#16a34a', label: 'OK' },
  partial: { bg: '#fef3c7', fg: '#b45309', dot: '#d97706', label: 'Partial' },
  failed:  { bg: '#fee2e2', fg: '#b91c1c', dot: '#dc2626', label: 'Failed' },
  running: { bg: '#dbeafe', fg: '#1d4ed8', dot: '#2563eb', label: 'Running' },
};
function fmtRunTime(ts) {
  try {
    const d = ts && ts.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
    return d ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  } catch { return '—'; }
}
const runMs = (r) => (r.startedAt && r.startedAt.toMillis ? r.startedAt.toMillis() : (r.startedAt ? new Date(r.startedAt).getTime() : 0));

function ScrapeHealthPanel({ runs }) {
  const [open, setOpen] = useState(true);
  const th = { padding: '6px 8px', fontWeight: 600, position: 'sticky', top: 0, background: '#fff', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', verticalAlign: 'top', borderTop: `1px solid ${T.border}` };

  const sorted = [...runs].sort((a, b) => runMs(b) - runMs(a));
  const weekAgo = Date.now() - 7 * 864e5;
  const recent = sorted.filter(r => runMs(r) >= weekAgo);
  const c = recent.reduce((a, r) => (a[r.status] = (a[r.status] || 0) + 1, a), {});
  const leads7 = recent.reduce((a, r) => a + (r.qualifiedCount || 0), 0);
  const todayKey = new Date().toISOString().slice(0, 10);
  const today = sorted.filter(r => r.dateKey === todayKey);

  const pill = (acct) => {
    const r = today.find(x => x.account === acct);
    const s = r ? (HEALTH_STATUS[r.status] || HEALTH_STATUS.failed) : { bg: '#f1f5f9', fg: '#94a3b8', dot: '#cbd5e1', label: 'No run yet' };
    return (
      <span key={acct} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.fg }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: s.dot }} />
        {acct} · {r ? `${s.label}${r.qualifiedCount != null ? ` · ${r.qualifiedCount}` : ''}` : 'No run yet'}
      </span>
    );
  };

  return (
    <div style={{ borderBottom: `1px solid ${T.border}`, background: '#fff' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 28px', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textMid }}>Scrape Health</span>
        {pill('VA')}
        {pill('VA-2')}
        <span style={{ fontSize: 12, color: T.textDim }}>
          7d: <b style={{ color: '#15803d' }}>{c.success || 0}</b> ok · <b style={{ color: '#b45309' }}>{c.partial || 0}</b> partial · <b style={{ color: '#b91c1c' }}>{c.failed || 0}</b> failed · {leads7} leads
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textMid }}>{open ? '▾' : '▸'} {sorted.length} runs</span>
      </div>
      {open && (
        <div style={{ maxHeight: 300, overflowY: 'auto', padding: '0 28px 14px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: T.text }}>
            <thead>
              <tr style={{ color: T.textDim, textAlign: 'left' }}>
                <th style={th}>Started</th>
                <th style={th}>Account</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Qualified</th>
                <th style={{ ...th, textAlign: 'right' }}>Raw</th>
                <th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const s = HEALTH_STATUS[r.status] || HEALTH_STATUS.failed;
                return (
                  <tr key={r.id}>
                    <td style={td}>{fmtRunTime(r.startedAt)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{r.account}</td>
                    <td style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.fg, fontWeight: 600, fontSize: 11 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: s.dot }} />{s.label}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: r.qualifiedCount > 0 ? T.text : T.textDim }}>{r.qualifiedCount != null ? r.qualifiedCount : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', color: T.textMid }}>{r.rawCount != null ? r.rawCount : '—'}</td>
                    <td style={{ ...td, color: T.textDim, maxWidth: 320 }}>{r.error || (r.source === 'backfill' ? 'reconstructed from logs' : '')}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, color: T.textDim, padding: '16px 8px' }}>No scrape runs recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function NdSellerLeadsDashboard() {
  const [rawLeads, setRawLeads]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeSignal, setActiveSignal] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeKeyword, setActiveKeyword] = useState('all');
  const [search, setSearch]       = useState('');
  const [preset, setPreset]       = useState('all');
  const [seenIds, setSeenIds]     = useState(() => loadSeen());
  const [onlyNew, setOnlyNew]     = useState(false);
  const [scrapeRuns, setScrapeRuns] = useState([]);

  const markSeen = (id) => {
    setSeenIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev); next.add(id); saveSeen(next); return next;
    });
  };
  const markAllSeen = () => {
    setSeenIds(prev => {
      const next = new Set(prev);
      rawLeads.forEach(l => next.add(l.id));
      saveSeen(next); return next;
    });
  };

  const deleteLead = async (id) => {
    try { await deleteDoc(doc(db, 'ndQualifiedLeadsV2', id)); }
    catch (err) { console.error('Delete failed:', err); alert('Delete failed: ' + err.message); }
  };

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'ndQualifiedLeadsV2')), snap => {
      setRawLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Scrape run-health records (powers the Scrape Health panel).
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ndSellerScrapeRuns'), orderBy('startedAt', 'desc'), limit(120)),
      snap => setScrapeRuns(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => console.error('ndSellerScrapeRuns subscription error:', e)
    );
    return () => unsub();
  }, []);

  const leads = useMemo(() => dedupeSellerLeads(rawLeads), [rawLeads]);

  // All sidebar counts exclude AI-flagged junk (hidden=true) so they match the feed.
  const visibleLeads = useMemo(() => leads.filter(l => l.hidden !== true), [leads]);

  const signalCounts = useMemo(() => {
    const c = { all: visibleLeads.length };
    SIGNALS.forEach(s => { c[s.key] = 0; });
    visibleLeads.forEach(l => detectSignals(l.postText).forEach(s => { c[s.key]++; }));
    return c;
  }, [visibleLeads]);

  const keywordCounts = useMemo(() => {
    const c = {};
    visibleLeads.forEach(l => (l.keywords || []).forEach(k => { c[k] = (c[k] || 0) + 1; }));
    return c;
  }, [visibleLeads]);
  const keywords = useMemo(() => Object.keys(keywordCounts).sort(), [keywordCounts]);

  // Count leads per category (any of the category's keywords match)
  const categoryCounts = useMemo(() => {
    const c = { all: visibleLeads.length };
    KEYWORD_CATEGORIES.forEach(cat => { c[cat.key] = 0; });
    visibleLeads.forEach(l => {
      const seen = new Set();
      (l.keywords || []).forEach(k => {
        const cat = KEYWORD_TO_CATEGORY[k];
        if (cat && !seen.has(cat.key)) { c[cat.key]++; seen.add(cat.key); }
      });
    });
    return c;
  }, [visibleLeads]);

  const dateRange = useMemo(() => getPresetRange(preset), [preset]);

  const newCount = useMemo(() => leads.filter(l => !seenIds.has(l.id)).length, [leads, seenIds]);

  const filtered = useMemo(() => {
    return leads
      .filter(l => {
        // AI-flagged junk (hidden=true) never appears in the dashboard.
        // The doc stays in Firestore so it can be audited / un-hidden later.
        if (l.hidden === true) return false;
        if (onlyNew && seenIds.has(l.id)) return false;
        if (activeSignal !== 'all' && !detectSignals(l.postText).some(h => h.key === activeSignal)) return false;
        if (activeCategory !== 'all') {
          const cat = KEYWORD_CATEGORIES.find(c => c.key === activeCategory);
          if (!cat) return false;
          const inCategory = (l.keywords || []).some(k => cat.keywords.includes(k));
          if (!inCategory) return false;
        }
        if (activeKeyword !== 'all' && !(l.keywords || []).includes(activeKeyword)) return false;
        if (!leadInPresetRange(l, dateRange)) return false;
        const q = search.toLowerCase();
        if (q && !l.author?.toLowerCase().includes(q) && !l.postText?.toLowerCase().includes(q) && !l.neighborhood?.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort(compareByScore);
  }, [leads, activeSignal, activeCategory, activeKeyword, dateRange, search, onlyNew, seenIds]);

  return (
    <div style={{
      display: 'flex', minHeight: '100vh', background: T.bg, color: T.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
    }}>
      {/* Global scrollbar styling */}
      <style>{`
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.10); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.20); }
        input::placeholder { color: ${T.textDim}; }
      `}</style>

      {/* ── Sidebar ── */}
      <aside style={{
        width: 280, flexShrink: 0, background: T.bgSidebar,
        borderRight: `1px solid ${T.border}`,
        display: 'flex', flexDirection: 'column', height: '100vh', position: 'sticky', top: 0,
      }}>

        {/* Brand */}
        <div style={{ padding: '24px 22px 18px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 7,
              background: 'linear-gradient(135deg, #a78bfa, #ec4899)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, boxShadow: '0 4px 14px rgba(167,139,250,0.4)',
            }}>🏘️</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.3px' }}>Seller Signals</div>
              <div style={{ fontSize: 11, color: T.textDim }}>Pre-distress leads</div>
            </div>
          </div>
          {!loading && (
            <div style={{ marginTop: 14, padding: '8px 11px', background: 'rgba(52,211,153,0.08)',
              border: '1px solid rgba(52,211,153,0.2)', borderRadius: 7,
              fontSize: 11, color: '#34d399', fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
              <span>✓ {visibleLeads.length} unique</span>
              {rawLeads.length !== visibleLeads.length && (
                <span style={{ color: 'rgba(52,211,153,0.5)' }}>−{rawLeads.length - visibleLeads.length} hidden/dupe</span>
              )}
            </div>
          )}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 10px 24px' }}>

          {/* Time range — horizontal segmented control */}
          <div style={{ marginBottom: 18 }}>
            <div style={sectionLabel}>Time</div>
            <div style={{ display: 'flex', gap: 4, padding: 3, background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${T.border}`, borderRadius: 8 }}>
              {PRESETS.map(p => (
                <button key={p.key} onClick={() => setPreset(p.key)} style={{
                  flex: 1, padding: '5px 0', border: 'none', borderRadius: 6, cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 600,
                  background: preset === p.key ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: preset === p.key ? T.text : T.textMid,
                  transition: 'all 0.12s',
                }}>{p.label}</button>
              ))}
            </div>
          </div>

          {/* Categories — every keyword grouped by life-event bucket */}
          <div style={{ marginBottom: 18 }}>
            <div style={sectionLabel}>Category</div>
            <SidebarItem
              active={activeCategory === 'all' && activeKeyword === 'all'}
              onClick={() => { setActiveCategory('all'); setActiveKeyword('all'); }}
              label="All categories"
              count={visibleLeads.length}
            />
            {KEYWORD_CATEGORIES.map(cat => {
              const catActive = activeCategory === cat.key;
              const catCount  = categoryCounts[cat.key] || 0;
              return (
                <div key={cat.key} style={{ marginTop: 4 }}>
                  <SidebarItem
                    emoji={cat.emoji}
                    active={catActive && activeKeyword === 'all'}
                    onClick={() => {
                      // Toggle: click active category to collapse back to "all"
                      if (catActive) { setActiveCategory('all'); setActiveKeyword('all'); }
                      else            { setActiveCategory(cat.key); setActiveKeyword('all'); }
                    }}
                    label={cat.label}
                    count={catCount}
                    color={cat.color}
                  />
                  {/* Drill-down: show keywords under the active category */}
                  {catActive && (
                    <div style={{ marginLeft: 14, marginTop: 2, marginBottom: 4,
                      borderLeft: `1px solid ${T.border}`, paddingLeft: 6 }}>
                      {cat.keywords.map(k => {
                        const kCount = keywordCounts[k] || 0;
                        return (
                          <div key={k} onClick={() => setActiveKeyword(activeKeyword === k ? 'all' : k)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '4px 9px', borderRadius: 5, cursor: 'pointer', userSelect: 'none',
                              fontSize: 11.5,
                              background: activeKeyword === k ? `${cat.color}22` : 'transparent',
                              color: activeKeyword === k ? cat.color : (kCount > 0 ? T.textMid : T.textDim),
                              fontWeight: activeKeyword === k ? 600 : 400,
                              transition: 'all 0.1s',
                            }}
                            onMouseEnter={e => { if (activeKeyword !== k) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                            onMouseLeave={e => { if (activeKeyword !== k) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {k}
                            </span>
                            <span style={{ fontSize: 10, color: T.textDim,
                              fontVariantNumeric: 'tabular-nums', flexShrink: 0, marginLeft: 6 }}>
                              {kCount}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: T.bg, color: T.text }}>

        {/* Topbar */}
        <div style={{
          padding: '20px 28px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', gap: 16, background: T.bg,
          position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(12px)',
        }}>
          <div style={{ flex: 1, maxWidth: 520, position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
              color: T.textDim, fontSize: 14,
            }}>⌕</span>
            <input
              placeholder="Search posts, names, or neighborhoods…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 14px 9px 36px',
                background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 9,
                fontSize: 13, color: T.text, outline: 'none', transition: 'border-color 0.12s',
              }}
              onFocus={e => e.target.style.borderColor = T.borderHi}
              onBlur={e => e.target.style.borderColor = T.border}
            />
          </div>
          {/* New-only toggle */}
          {newCount > 0 && (
            <button
              onClick={() => setOnlyNew(o => !o)}
              style={{
                padding: '6px 11px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
                background: onlyNew ? T.accent : T.accentGlow,
                color: onlyNew ? '#fff' : T.accent,
                transition: 'all 0.12s',
              }}
            >
              {onlyNew ? `Showing ${newCount} new only` : `${newCount} new`}
            </button>
          )}

          {/* Mark all read */}
          {newCount > 0 && (
            <button
              onClick={markAllSeen}
              style={{
                padding: '6px 11px', borderRadius: 7, border: `1px solid ${T.border}`,
                cursor: 'pointer', fontSize: 11.5, fontWeight: 500, whiteSpace: 'nowrap',
                background: '#fff', color: T.textMid,
              }}
              title="Mark every lead as seen"
            >
              Mark all read
            </button>
          )}

          <span style={{ fontSize: 12.5, color: T.textMid, whiteSpace: 'nowrap', fontWeight: 500 }}>
            {loading ? '—' : <><span style={{ color: T.text, fontWeight: 700 }}>{filtered.length}</span> {filtered.length === 1 ? 'lead' : 'leads'}</>}
          </span>
        </div>

        {/* Scrape health */}
        <ScrapeHealthPanel runs={scrapeRuns} />

        {/* Feed */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '100px 0', textAlign: 'center', color: T.textDim, fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '100px 0', textAlign: 'center', color: T.textDim, fontSize: 13 }}>
              No leads match your filters.
            </div>
          ) : (
            filtered.map(entry => (
              <LeadRow
                key={entry.id}
                entry={entry}
                isNew={!seenIds.has(entry.id)}
                onMarkSeen={markSeen}
                onDelete={deleteLead}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

const sectionLabel = {
  fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: '0.1em',
  textTransform: 'uppercase', padding: '0 6px', marginBottom: 8,
};
